<template>
  <Toast />
  <Dialog
    v-model:visible="showEventModal"
    modal
    :header="isEditing ? 'Edit Event' : 'Add New Event'"
    :style="{ width: '500px' }"
    @hide="resetForm"
  >
    <form
      class="p-fluid"
      @submit.prevent="submitEvent"
    >
      <div
        v-if="showFields('title')"
        class="field mb-4"
      >
        <label
          for="eventTitle"
          class="block mb-2"
        >Event Title <span class="text-red-500">*</span></label>
        <InputText
          id="eventTitle"
          v-model="eventForm.title"
          placeholder="Event Title"
          fluid
          required
        />
      </div>

      <div
        v-if="showFields('description')"
        class="field mb-4"
      >
        <label
          for="eventDescription"
          class="block mb-2"
        >Event Description <span class="text-red-500">*</span></label>
        <Textarea
          id="eventDescription"
          v-model="eventForm.description"
          placeholder="Event Description"
          rows="3"
          fluid
        />
      </div>

      <div
        v-if="showFields('eventType')"
        class="field mb-4"
      >
        <label
          for="eventType"
          class="block mb-2"
        >Event Type <span class="text-red-500">*</span></label>
        <Dropdown
          id="eventType"
          v-model="eventForm.eventType"
          :options="eventTypes"
          optionLabel="name"
          optionValue="value"
          placeholder="Select Event Type"
          fluid
          required
        />
      </div>

      <div
        v-if="showFields('eventDate')"
        class="field mb-4"
      >
        <label
          for="eventDate"
          class="block mb-2"
        >Event Date <span class="text-red-500">*</span></label>
        <Calendar
          id="eventDate"
          v-model="eventForm.eventDate"
          placeholder="Select Date"
          fluid
          required
        />
      </div>

      <div
        v-if="showFields('eventTime')"
        class="field mb-4"
      >
        <label
          for="eventTime"
          class="block mb-2"
        >Event Time <span class="text-red-500">*</span></label>

        <InputText
          id="eventTitle"
          v-model="eventForm.eventTime"
          placeholder="Event Time"
          fluid
          required
        />
      </div>

      <div
        v-if="showFields('address')"
        class="field mb-4"
      >
        <label
          for="eventLocation"
          class="block mb-2"
        >Event Location <span class="text-red-500">*</span></label>
        <BaseGoogleInternationalAddress
          v-model:address="eventForm.address"
          :show-address-label="false"
          :disabled-input-field="false"
        />
      </div>

      <div
        v-if="showFields('url')"
        class="field mb-4"
      >
        <label
          for="eventUrl"
          class="block mb-2"
        >Event URL <span class="text-red-500">*</span></label>
        <InputText
          id="eventUrl"
          v-model="eventForm.url"
          placeholder="Event URL"
          fluid
          required
        />
      </div>

      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          severity="secondary"
          type="button"
          @click="closeDialog"
        />
        <Button
          :loading="isSubmitting"
          :disabled="isSubmitting"
          :label="eventForm.id ? 'Update' : 'Add'"
          type="submit"
          severity="primary"
          @click="submitEvent"
        />
      </div>
    </form>
  </Dialog>
</template>

<script setup lang='ts'>
const { createEvent: createEventToDB, updateEvent: updateEventInDB } = useDashboardEvents()
const showEventModal = defineModel('showEventModal', {
  type: Boolean,
  default: false,
})
const eventForm = defineModel('eventForm', {
  type: Object,
  default: () => ({
    id: null,
    title: '',
    description: '',
    eventType: 'IN_PERSON',
    eventTime: '',
    eventDate: null,
    address: null,
    url: '',
  }),
})
const emits = defineEmits(['init'])
const dialogVisible = ref(false)
const isEditing = ref(false)
const toast = useToast()
const eventTypes = [
  { name: 'In Person', value: 'IN_PERSON' },
  { name: 'Virtual', value: 'VIRTUAL' },
  { name: 'Hybrid', value: 'HYBRID' },
]

const showFields = (field: string) => {
  const virtualFields = ['url', 'eventTime', 'eventDate', 'eventType', 'title', 'description']
  const inPersonFields = ['address', 'eventTime', 'eventDate', 'eventType', 'title', 'description']
  const hybridFields = ['url', 'eventTime', 'eventDate', 'eventType', 'title', 'description', 'address']

  if (eventForm.value.eventType === 'VIRTUAL') {
    return virtualFields.includes(field)
  }
  else if (eventForm.value.eventType === 'IN_PERSON') {
    return inPersonFields.includes(field)
  }
  else {
    return hybridFields.includes(field)
  }
}

const isSubmitting = ref(false)
const submitEvent = async () => {
  try {
    isSubmitting.value = true
    //  check required fields

    const fields = [{
      name: 'Title',
      key: 'title',
    }, {
      name: 'Description',
      key: 'description',
    }, {
      name: 'Event Type',
      key: 'eventType',
    }, {
      name: 'Event Date',
      key: 'eventDate',
    }, {
      name: 'Event Time',
      key: 'eventTime',
    }, {
      name: 'Event Location',
      key: 'address',
    }, {
      name: 'Event URL',
      key: 'url',
    }]
    const requiredFields: string[] = []

    fields.forEach((field) => {
      if (showFields(field.key)) {
        console.log(field)
        if (field.key === 'address') {
          console.log(eventForm.value.address.fullAddress)
          if (!eventForm.value[field.key]) {
            requiredFields.push(field.name)
          }
        }
        else if (!eventForm.value[field.key]) {
          requiredFields.push(field.name)
        }
      }
    })
    console.log(requiredFields)
    if (requiredFields.length > 0) {
      throw new Error(`Please fill in all required fields: ${requiredFields.join(', ')}`)
      return
    }

    if (eventForm.value.id) {
      await updateEventInDB(eventForm.value)
    }
    else {
      await createEventToDB(eventForm.value)
    }

    emits('init')
    showEventModal.value = false
    eventForm.value = {}
    isSubmitting.value = false
  }
  catch (error: unknown) {
    console.error(error)
    isSubmitting.value = false
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error instanceof Error ? error.message : 'An unknown error occurred',
      life: 3000,
    })
  }
}

// const validateForm = () => {
//   return Object.values(eventForm).every(value => value !== null && value !== '')
// }

const closeDialog = () => {
  dialogVisible.value = false
}

const resetForm = () => {
  eventForm.value = {}
  isEditing.value = false
}

// defineExpose({
//   showDialog,
// })
</script>
